import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrg } from '../lib/org';
import { useToast } from '../components/toast';
import * as orgApi from '../api/organizations';
import HostSetup from '../components/HostSetup';
import AudienceSettingsForm from '../components/AudienceSettingsForm';
import ComplianceProfileForm from '../components/ComplianceProfileForm';
import OrgTypePicker from '../components/OrgTypePicker';
import * as billingApi from '../api/billing';
import { cn, ui } from '../lib/ui';
import { orgAdminMayChangeType, orgTypePolicy } from '../lib/orgTypes';
import {
  furthest,
  initialStepId,
  isReachable,
  nextStepId,
  previousStepId,
  stepRank,
  visibleSteps,
} from '../lib/setupWizard';
import { fieldErrorsFrom } from '../lib/formErrors';
import { NONPROFIT_CHANGE_WARNING, afterTypeChange } from '../lib/typeChange';
import {
  Spinner,
  ErrorAlert,
  EmptyState,
  Field,
  SelectField,
  TextareaField,
  UserSearchPicker,
} from '../components/ui';

/**
 * The organization setup wizard (docs/Decisions.md D63 item 5, D64): the type
 * and the plan first, then only what they make relevant. Every step has Back;
 * the progress list reopens any step already reached and none past it; the
 * furthest step is saved on the org, so the wizard resumes where it was left
 * (including after a Stripe checkout).
 */
function StepIndicator({ steps, current, reached, onSelect }) {
  return (
    <nav className="mb-8" aria-label="Setup progress">
      <ol className="m-0 flex list-none flex-wrap gap-1 p-0">
        {steps.map((step, i) => {
          const active = step.id === current;
          const reachable = isReachable(step.id, reached);
          const done = !active && stepRank(step.id) < stepRank(reached);
          // The badge is decoration; the name comes from the hidden "Step N:"
          // plus the visible label, so it contains the visible text.
          const badge = (
            <span
              aria-hidden="true"
              className={cn(
                'inline-flex size-7 items-center justify-center rounded-full text-caption font-semibold',
                active || done
                  ? 'bg-dcon-brand text-dcon-on-brand'
                  : reachable
                    ? 'border-2 border-dcon-brand text-dcon-brand'
                    : 'bg-z-sunken text-dcon-ink-muted'
              )}
            >
              {done ? '✓' : i + 1}
            </span>
          );
          const text = (
            <span
              className={cn(
                'text-body-s',
                active
                  ? 'font-semibold text-dcon-ink'
                  : reachable
                    ? 'text-dcon-link'
                    : 'text-dcon-ink-muted'
              )}
            >
              <span className="sr-only">Step {i + 1}: </span>
              {step.label}
              {done && <span className="sr-only"> (done)</span>}
            </span>
          );
          return (
            <li className="flex items-center gap-1" key={step.id}>
              {reachable && !active ? (
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 rounded-dcon-md border-0 bg-transparent p-1 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-dcon-focus"
                  onClick={() => onSelect(step.id)}
                >
                  {badge}
                  {text}
                </button>
              ) : (
                <span
                  className="inline-flex items-center gap-1 p-1"
                  aria-current={active ? 'step' : undefined}
                  aria-disabled={!reachable || undefined}
                  title={reachable ? undefined : 'Finish the earlier steps first'}
                >
                  {badge}
                  {text}
                </span>
              )}
              {i < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn('mx-1 h-0.5 w-6', done ? 'bg-dcon-brand' : 'bg-dcon-line-strong')}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Back, then any other buttons a step adds. */
function BackButton({ onBack }) {
  if (!onBack) return null;
  return (
    <button className={ui.btnGhost} onClick={onBack} type="button">
      Back
    </button>
  );
}

function StepCard({ title, intro, children, wide = false }) {
  return (
    <div className={cn(ui.card, wide ? 'max-w-2xl' : 'max-w-xl')}>
      <h2 className="mt-0 mb-2 text-title-m">{title}</h2>
      {intro && <p className={cn(ui.muted, 'text-body-s', 'mb-4')}>{intro}</p>}
      {children}
    </div>
  );
}

function TypeStep({ org, onNext, onBack, onOrgChange }) {
  const toast = useToast();
  const [type, setType] = useState(org.orgType || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const locked = Boolean(org.orgType) && !orgAdminMayChangeType(org);

  async function handleContinue() {
    if (!type) {
      setError('Choose the type that fits your organization');
      return;
    }
    if (type === org.orgType) {
      onNext();
      return;
    }
    setSaving(true);
    try {
      const from = org.orgType;
      const data = await orgApi.updateOrganization(org.orgId, { orgType: type });
      onOrgChange(data.organization);
      toast.success(`${orgTypePolicy(type).label} organization`);
      const note = await afterTypeChange(org.orgId, from, type);
      if (note) toast.info(note);
      onNext();
    } catch (err) {
      setError(fieldErrorsFrom(err).orgType || err.message || 'Failed to save the type');
    } finally {
      setSaving(false);
    }
  }

  return (
    <StepCard
      title="What kind of organization is this?"
      intro="Your type decides what the rest of setup asks for and which payments you can take. You can change it until you have members."
      wide
    >
      {locked && (
        <div className={cn(ui.alertInfo, 'mb-4')}>
          Your organization has members, so only the platform team can change its type.
        </div>
      )}
      <OrgTypePicker
        value={type}
        onChange={value => {
          setType(value);
          setError(null);
        }}
        disabled={locked}
      />
      {error && (
        <span className={ui.fieldError} role="alert">
          {error}
        </span>
      )}
      {org.orgType === 'nonprofit' && type && type !== 'nonprofit' && (
        <p className={cn(ui.alertWarning, 'mt-4')}>{NONPROFIT_CHANGE_WARNING}</p>
      )}
      {org.orgType && type && type !== org.orgType && orgTypePolicy(org.orgType).profile && (
        <p className={cn(ui.alertWarning, 'mt-4')}>
          Changing the type clears the {orgTypePolicy(org.orgType).label.toLowerCase()} profile you
          started.
        </p>
      )}
      <div className={cn(ui.row, 'mt-4')}>
        <button className={ui.btnPrimary} onClick={handleContinue} disabled={saving} type="button">
          {saving ? 'Saving...' : 'Continue'}
        </button>
        <BackButton onBack={onBack} />
      </div>
    </StepCard>
  );
}

const TIER_LABELS = {
  base: { name: 'Starter', desc: 'Events, calendar, groups, and video hosting' },
  mid: { name: 'Professional', desc: 'Everything in Starter plus inbox, forums, and tasks' },
  top: {
    name: 'Enterprise',
    desc: 'All features including verification, custom roles, SAML, and API access',
  },
};

function PlanStep({ org, onNext, onBack }) {
  const toast = useToast();
  const [tiers, setTiers] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [starting, setStarting] = useState(null);
  const [unavailable, setUnavailable] = useState({});

  useEffect(() => {
    billingApi
      .listTiers()
      .then(data => setTiers(data.tiers || []))
      .catch(err => setLoadError(err));
  }, []);

  const currentTier = org.tier || 'base';

  async function handleSelectTier(tierId) {
    if (tierId === currentTier) {
      onNext();
      return;
    }
    if (tierId === 'base') {
      toast.info('To move back to Starter, use Org settings -> Billing and plan after setup.');
      return;
    }
    setStarting(tierId);
    try {
      const origin = window.location.origin;
      const data = await billingApi.createCheckout(
        tierId,
        `${origin}/org/setup?step=branding&billing=success`,
        `${origin}/org/setup?step=plan&billing=cancelled`
      );
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Unable to start checkout');
      }
    } catch (err) {
      if (err.details?.planUnavailable) {
        setUnavailable(prev => ({ ...prev, [tierId]: err.message }));
      }
      toast.error(err.message || 'Checkout failed');
    } finally {
      setStarting(null);
    }
  }

  if (loadError) return <ErrorAlert error={loadError} />;
  if (!tiers) return <Spinner label="Loading plans..." />;

  return (
    <StepCard
      title="Choose a plan"
      intro={
        org.stripeSubscriptionId
          ? `Your organization is on the ${TIER_LABELS[currentTier]?.name || currentTier} plan.`
          : 'Start on the free Starter plan or upgrade for more features. The next steps show only what your plan includes.'
      }
    >
      <div className="flex flex-col gap-3">
        {['base', 'mid', 'top'].map(tierId => {
          const label = TIER_LABELS[tierId];
          const tierData = tiers.find(t => t.tierId === tierId);
          const isCurrent = tierId === currentTier;
          const purchasable = tierId === 'base' || tierData?.purchasable;
          const blocked = unavailable[tierId] || (!purchasable && !isCurrent);
          return (
            <div
              key={tierId}
              className={cn(
                'flex items-center justify-between gap-3 rounded-z-card p-4',
                isCurrent ? 'border-2 border-dcon-brand' : 'border border-z-line'
              )}
            >
              <div>
                <strong>{label.name}</strong>
                {isCurrent && <span className={cn(ui.pillPrimary, 'ml-2')}>Current</span>}
                <div className={cn(ui.muted, 'text-body-s', 'mt-1')}>{label.desc}</div>
                {blocked && !isCurrent && tierId !== 'base' && (
                  <div className={ui.hint}>
                    Not available for online signup yet. Continue on{' '}
                    {TIER_LABELS[currentTier]?.name} and upgrade later from Org settings.
                  </div>
                )}
                {tierId === 'base' && !isCurrent && (
                  <div className={ui.hint}>
                    To move back to Starter, use Manage billing in Org settings after setup.
                  </div>
                )}
              </div>
              {isCurrent ? (
                <button
                  className={cn(ui.btnPrimary, ui.btnSm)}
                  onClick={() => handleSelectTier(tierId)}
                  type="button"
                >
                  Continue
                </button>
              ) : (
                tierId !== 'base' &&
                !blocked && (
                  <button
                    className={cn(ui.btnSecondary, ui.btnSm)}
                    onClick={() => handleSelectTier(tierId)}
                    disabled={Boolean(starting)}
                    type="button"
                  >
                    {starting === tierId ? 'Redirecting...' : `Choose ${label.name}`}
                  </button>
                )
              )}
            </div>
          );
        })}
      </div>
      <div className={cn(ui.row, 'mt-4')}>
        <BackButton onBack={onBack} />
      </div>
    </StepCard>
  );
}

// An organization's brand colours are content (data the org chooses), not
// theme. The picker needs some starting value; it starts from the platform
// brand token as rendered, so no colour literal lives in source.
function themeBrandColor() {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue('--brand').trim();
  } catch {
    return '';
  }
}

function ColorField({ label, value, onChange, hint }) {
  const fallback = themeBrandColor();
  return (
    <div className="mb-3">
      <span className={ui.label}>{label}</span>
      <div className="flex items-center gap-2">
        <input
          className="size-10 cursor-pointer rounded-dcon-sm border border-dcon-line-strong bg-z-surface p-0.5"
          type="color"
          aria-label={`${label} picker`}
          value={(value || fallback || '').toLowerCase()}
          onChange={e => onChange(e.target.value)}
        />
        <input
          className={cn(ui.input, 'w-32! font-mono')}
          type="text"
          aria-label={`${label} hex value`}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={fallback}
          maxLength={7}
        />
        {value && (
          <button
            type="button"
            className={cn(ui.btnSecondary, ui.btnSm)}
            onClick={() => onChange('')}
          >
            Clear
          </button>
        )}
      </div>
      {hint && <span className={cn(ui.muted, 'text-body-s')}>{hint}</span>}
    </div>
  );
}

function BrandingStep({ org, onNext, onSkip, onBack }) {
  const toast = useToast();
  const [name, setName] = useState(org.name || '');
  const [description, setDescription] = useState(org.description || '');
  const [logoUrl, setLogoUrl] = useState(org.logoUrl || '');
  const branding = org.branding || {};
  const [primaryColor, setPrimaryColor] = useState(branding.primaryColor || '');
  const [secondaryColor, setSecondaryColor] = useState(branding.secondaryColor || '');
  const [favicon, setFavicon] = useState(branding.favicon || '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const brandingPayload = {};
      if (primaryColor) brandingPayload.primaryColor = primaryColor;
      if (secondaryColor) brandingPayload.secondaryColor = secondaryColor;
      if (logoUrl.trim()) brandingPayload.logo = logoUrl.trim();
      if (favicon.trim()) brandingPayload.favicon = favicon.trim();

      await orgApi.updateOrganization(org.orgId, {
        name: name.trim(),
        description: description.trim(),
        logoUrl: logoUrl.trim() || undefined,
        branding: Object.keys(brandingPayload).length ? brandingPayload : undefined,
      });
      toast.success('Branding saved');
      onNext();
    } catch (err) {
      toast.error(err.message || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={cn(ui.card, 'max-w-xl')}>
      <h2 className="mt-0 mb-2 text-title-m">Branding</h2>
      <p className={cn(ui.muted, 'text-body-s', 'mb-4')}>
        Set your organization&apos;s name, description, logo, and brand colors.
      </p>

      <Field
        label="Organization name"
        value={name}
        onChange={e => setName(e.target.value)}
        required
        maxLength={200}
      />
      <TextareaField
        label="Description"
        value={description}
        onChange={e => setDescription(e.target.value)}
        maxLength={1000}
        rows={3}
        hint="A short description shown in the organization directory."
      />
      <Field
        label="Logo URL"
        value={logoUrl}
        onChange={e => setLogoUrl(e.target.value)}
        placeholder="https://example.com/logo.png"
        hint="A URL to your organization logo. You can update this later."
      />
      <Field
        label="Favicon URL"
        value={favicon}
        onChange={e => setFavicon(e.target.value)}
        placeholder="https://example.com/favicon.ico"
        hint="A URL to your favicon. Shown in the browser tab for your org pages."
      />

      <div className="mt-2 mb-2">
        <h3 className="mt-0 text-body-s mb-2 font-semibold">Brand colors</h3>
        <ColorField
          label="Primary color"
          value={primaryColor}
          onChange={setPrimaryColor}
          hint="Used for buttons, links, and accents."
        />
        <ColorField
          label="Secondary color"
          value={secondaryColor}
          onChange={setSecondaryColor}
          hint="Used for secondary elements and backgrounds."
        />
      </div>

      <div className={cn(ui.row, 'mt-4')}>
        <button className={ui.btnPrimary} onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? 'Saving...' : 'Save and continue'}
        </button>
        <button className={ui.btnSecondary} onClick={onSkip} type="button">
          Skip
        </button>
        <BackButton onBack={onBack} />
      </div>
    </div>
  );
}

function DomainStep({ onNext, onSkip, onBack }) {
  return (
    <StepCard
      title="Your domain"
      intro="Optionally use a domain your organization owns. Your website (your template site) and your member portal each get their own address on it, with its own certificate. You can also do this later from the website builder or organization settings, and rename either address there."
      wide
    >
      <HostSetup onNext={onNext} onSkip={onSkip} renderNav={() => <BackButton onBack={onBack} />} />
    </StepCard>
  );
}

function FeaturesStep({ org, planFeatures, onNext, onSkip, onBack }) {
  return (
    <StepCard
      title="Features"
      intro="Choose who outside your organization can see your events, and how many videos members can play. Open a feature to change it; each setting shows its default. Events, the forum and videos have no user limits, and forum access is set on each forum category."
      wide
    >
      <AudienceSettingsForm
        orgId={org.orgId}
        planFeatures={planFeatures}
        onSaved={onNext}
        renderActions={({ saving, save }) => (
          <div className={cn(ui.row, 'mt-4')}>
            <button className={ui.btnPrimary} onClick={save} disabled={saving} type="button">
              {saving ? 'Saving...' : 'Save and continue'}
            </button>
            <button className={ui.btnSecondary} onClick={onSkip} type="button">
              Keep the defaults
            </button>
            <BackButton onBack={onBack} />
          </div>
        )}
      />
    </StepCard>
  );
}

const PROFILE_INTRO = {
  nonprofit:
    'Donations go to your nonprofit, so donors and the State of California need to know who receives them. The platform team verifies these details before donations open. Your fee settings and Stripe account are in Org settings -> Payments.',
  investment:
    'Investment organizations are charged a flat fee for each service, never a share of a transaction, and keep offering materials to verified accredited members. The platform team verifies this profile before member services open.',
};

function ProfileStep({ org, onNext, onSkip, onBack, onOrgChange }) {
  const policy = orgTypePolicy(org.orgType);
  return (
    <StepCard
      title={policy.profile === 'nonprofit' ? 'Nonprofit profile' : 'Compliance profile'}
      intro={PROFILE_INTRO[policy.profile]}
      wide
    >
      <ComplianceProfileForm
        key={org.orgType}
        org={org}
        onSaved={updated => onOrgChange(updated)}
        renderActions={({ busy, saveDraft, submit, canSaveDraft }) => (
          <div className={cn(ui.row, 'mt-2')}>
            <button
              className={ui.btnPrimary}
              type="button"
              disabled={Boolean(busy)}
              onClick={async () => {
                if (await submit()) onNext();
              }}
            >
              {busy === 'submit' ? 'Submitting...' : 'Submit for review and continue'}
            </button>
            {canSaveDraft && (
              <button
                className={ui.btnSecondary}
                type="button"
                disabled={Boolean(busy)}
                onClick={async () => {
                  if (await saveDraft()) onNext();
                }}
              >
                {busy === 'draft' ? 'Saving...' : 'Save draft and continue'}
              </button>
            )}
            <button className={ui.btnSecondary} onClick={onSkip} type="button">
              Skip
            </button>
            <BackButton onBack={onBack} />
          </div>
        )}
      />
    </StepCard>
  );
}

function SignupStep({ org, onNext, onSkip, onBack }) {
  const toast = useToast();
  const [visibility, setVisibility] = useState(org.visibility || 'invite_only');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await orgApi.updateOrganization(org.orgId, { visibility });
      toast.success('Signup mode saved');
      onNext();
    } catch (err) {
      toast.error(err.message || 'Failed to save signup mode');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={cn(ui.card, 'max-w-xl')}>
      <h2 className="mt-0 mb-2 text-title-m">Signup Mode</h2>
      <p className={cn(ui.muted, 'text-body-s', 'mb-4')}>
        Choose how new members can join your organization.
      </p>

      <SelectField
        label="Visibility"
        value={visibility}
        onChange={e => setVisibility(e.target.value)}
        hint="Public organizations appear in the directory and anyone can request to join. Invite-only organizations are visible only to current members."
      >
        <option value="public">Public - anyone can join</option>
        <option value="invite_only">Invite only - members must be invited</option>
        <option value="import_only">Import only - members are imported from another org</option>
      </SelectField>

      <div className={cn(ui.row, 'mt-4')}>
        <button className={ui.btnPrimary} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save and continue'}
        </button>
        <button className={ui.btnSecondary} onClick={onSkip} type="button">
          Skip
        </button>
        <BackButton onBack={onBack} />
      </div>
    </div>
  );
}

function InviteStep({ org, onFinish, onBack }) {
  const toast = useToast();
  const [added, setAdded] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  async function handlePick(user) {
    try {
      await orgApi.addMember(org.orgId, user.userId, 'member');
      setAdded(prev => [...prev, { name: user.displayName || user.email, ok: true }]);
      toast.success(`${user.displayName || user.email} added`);
    } catch (err) {
      setAdded(prev => [
        ...prev,
        { name: user.displayName || user.email, ok: false, error: err.message },
      ]);
      toast.error(err.message || 'Failed to add member');
    }
  }

  async function handleEmailInvite(e) {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    try {
      await orgApi.inviteMember(org.orgId, email);
      setAdded(prev => [...prev, { name: email, ok: true }]);
      toast.success(`Invitation sent to ${email}`);
      setInviteEmail('');
    } catch (err) {
      setAdded(prev => [...prev, { name: email, ok: false, error: err.message }]);
      toast.error(err.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  }

  return (
    <div className={cn(ui.card, 'max-w-xl')}>
      <h2 className="mt-0 mb-2 text-title-m">Invite Members</h2>
      <p className={cn(ui.muted, 'text-body-s', 'mb-4')}>
        Add existing platform users or invite new people by email. You can also do this later from
        organization settings.
      </p>

      <div className="mb-6">
        <h3 className="mt-0 mb-2 text-body-s font-semibold">Search existing users</h3>
        <UserSearchPicker
          onPick={handlePick}
          exclude={added.filter(a => a.ok).map(a => a.userId)}
        />
      </div>

      <div className="border-t border-z-line pt-4">
        <h3 className="mt-0 mb-2 text-body-s font-semibold">Invite by email</h3>
        <form className="flex gap-2 items-end" onSubmit={handleEmailInvite}>
          <div className="flex-1">
            <input
              className={ui.input}
              type="email"
              aria-label="Email address to invite"
              placeholder="user@example.com"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              required
              disabled={inviting}
            />
          </div>
          <button
            className={ui.btnSecondary}
            type="submit"
            disabled={inviting || !inviteEmail.trim()}
          >
            {inviting ? 'Sending...' : 'Send invite'}
          </button>
        </form>
      </div>

      {added.length > 0 && (
        <div className="mt-4 text-body-s">
          {added.map((r, i) => (
            <div className="py-1" key={i}>
              {r.ok ? (
                <span className="text-dcon-success">
                  {'✓'} {r.name}
                </span>
              ) : (
                <span className="text-dcon-danger">
                  {'✗'} {r.name} - {r.error}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={cn(ui.row, 'mt-4')}>
        <button className={ui.btnPrimary} onClick={onFinish} type="button">
          {added.some(a => a.ok) ? 'Finish setup' : 'Skip and finish'}
        </button>
        <BackButton onBack={onBack} />
      </div>
    </div>
  );
}

export default function OrgSetupWizard() {
  const { activeOrg, refresh } = useOrg();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [org, setOrg] = useState(null);
  const [planFeatures, setPlanFeatures] = useState(null);
  const [step, setStep] = useState(null);
  const [reached, setReached] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadOrg = useCallback(async () => {
    if (!activeOrg?.orgId) return null;
    try {
      const [data, usage] = await Promise.all([
        orgApi.getOrg(activeOrg.orgId),
        billingApi.getUsage().catch(() => null),
      ]);
      const loaded = data.organization || data;
      setOrg(loaded);
      setPlanFeatures(usage?.features || null);
      return loaded;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [activeOrg?.orgId]);

  // First load: open the step in the URL when it is reachable (a return from
  // Stripe checkout), else where the admin left off.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadOrg().then(loaded => {
      if (cancelled || !loaded) return;
      const saved = loaded.setupProgress?.reached || null;
      const requested = searchParams.get('step');
      // Stripe sends the admin back to the step after the plan; count it reached.
      const fromCheckout = searchParams.get('billing') === 'success' ? requested : null;
      const furthestReached = furthest(saved, fromCheckout);
      setReached(furthestReached);
      const steps = visibleSteps(loaded, null);
      setStep(initialStepId(steps, requested, furthestReached));
      if (searchParams.get('billing') === 'success') toast.success('Plan updated');
    });
    return () => {
      cancelled = true;
    };
    // Only on first load and when the active org changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadOrg]);

  const steps = useMemo(() => (org ? visibleSteps(org, planFeatures) : []), [org, planFeatures]);

  // Keep the URL on the current step, so a reload or a Stripe return lands here.
  useEffect(() => {
    if (step && searchParams.get('step') !== step) {
      setSearchParams({ step }, { replace: true });
    }
  }, [step, searchParams, setSearchParams]);

  // A plan without feature settings hides the features step; never sit on a hidden one.
  useEffect(() => {
    if (step && steps.length && !steps.some(s => s.id === step)) {
      setStep(initialStepId(steps, null, reached));
    }
  }, [step, steps, reached]);

  function saveReached(next) {
    const value = furthest(reached, next);
    if (value === reached) return;
    setReached(value);
    // Best-effort: progress also lives in this page's state.
    orgApi.updateOrganization(org.orgId, { setupProgress: { reached: value } }).catch(() => {});
  }

  function goTo(id) {
    if (!id) return;
    setStep(id);
    window.scrollTo?.({ top: 0, behavior: 'smooth' });
  }

  function goNext() {
    const next = nextStepId(steps, step);
    if (!next) return;
    saveReached(next);
    goTo(next);
    loadOrg();
  }

  function goBack() {
    goTo(previousStepId(steps, step));
  }

  async function finish() {
    try {
      await orgApi.updateOrganization(org.orgId, {
        setupProgress: { reached: 'invite', completed: true },
      });
    } catch {
      // Finishing never blocks on saving progress.
    }
    refresh();
    navigate('/org/settings');
  }

  // Every state keeps the page title, so the page always has its heading.
  const title = <h1 className={ui.h1}>Set up {org?.name || 'your organization'}</h1>;

  if (!activeOrg) {
    return (
      <>
        {title}
        <EmptyState>No organization selected. Create or join an organization first.</EmptyState>
      </>
    );
  }

  if (loading || error || !org || !step) {
    return (
      <>
        {title}
        {error ? (
          <ErrorAlert error={error} />
        ) : loading || !step ? (
          <Spinner label="Loading organization..." />
        ) : (
          <EmptyState>Organization not found.</EmptyState>
        )}
      </>
    );
  }

  const onBack = previousStepId(steps, step) ? goBack : null;
  const common = { org, onNext: () => goNext(), onSkip: () => goNext(), onBack };
  const onOrgChange = updated => setOrg(prev => ({ ...prev, ...updated }));

  return (
    <>
      {title}
      <StepIndicator steps={steps} current={step} reached={reached} onSelect={goTo} />

      {step === 'type' && (
        <TypeStep org={org} onBack={null} onOrgChange={onOrgChange} onNext={() => goNext()} />
      )}
      {step === 'plan' && <PlanStep org={org} onNext={() => goNext()} onBack={onBack} />}
      {step === 'branding' && <BrandingStep {...common} />}
      {step === 'domain' && <DomainStep {...common} />}
      {step === 'signup' && <SignupStep {...common} />}
      {step === 'features' && <FeaturesStep {...common} planFeatures={planFeatures} />}
      {step === 'profile' && <ProfileStep {...common} onOrgChange={onOrgChange} />}
      {step === 'invite' && <InviteStep org={org} onFinish={finish} onBack={onBack} />}
    </>
  );
}
